import { useCallback, useEffect, useState, useRef } from 'react';
import { analytics } from '../services/analytics';

const TOKEN_KEY = 'artstash_token';
const REFRESH_TOKEN_KEY = 'artstash_refresh_token';
const USER_KEY = 'artstash_user';
const TOKEN_EXPIRY_KEY = 'artstash_token_expiry';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';
const KEYCLOAK_REDIRECT_URI = process.env.REACT_APP_KEYCLOAK_REDIRECT_URI;

export type AuthMode = 'keycloak' | 'format' | 'unknown';

interface User {
  email?: string;
  name?: string;
  preferred_username?: string;
  sub?: string;
  [key: string]: any;
}

export function getStoredToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function useAuth() {
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<User | null>(() => {
    const stored = sessionStorage.getItem(USER_KEY);
    return stored ? JSON.parse(stored) : null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<AuthMode>('unknown');
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAuthenticated = !!token;

  // Always fetch auth mode from backend on mount
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/auth/mode`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const mode: AuthMode = data.mode === 'format' ? 'format' : 'keycloak';
        setAuthMode(mode);
      })
      .catch(() => {
        if (cancelled) return;
        // If backend is unreachable, default to format (dev mode) so the
        // user isn't stuck on a Keycloak redirect that will also fail.
        setAuthMode('format');
      });
    return () => { cancelled = true; };
  }, []);

  // Store tokens and extract user info from JWT
  const storeTokens = useCallback((data: any) => {
    const accessToken = data.access_token || data.token || data.accessToken;
    const refreshToken = data.refresh_token || data.refreshToken;
    const expiresIn = data.expires_in || 300;

    if (accessToken) {
      sessionStorage.setItem(TOKEN_KEY, accessToken);
      setToken(accessToken);
    }
    if (refreshToken) {
      sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    const expiry = Date.now() + expiresIn * 1000;
    sessionStorage.setItem(TOKEN_EXPIRY_KEY, String(expiry));

    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const userInfo: User = {
          email: payload.email,
          name: payload.name || payload.preferred_username,
          preferred_username: payload.preferred_username,
          sub: payload.sub,
        };
        sessionStorage.setItem(USER_KEY, JSON.stringify(userInfo));
        setUser(userInfo);
      } catch {
        // Token might not be a standard JWT — that's OK
      }
    }
  }, []);

  // Dev-mode login — get a local token without Keycloak
  const loginDev = useCallback(async (email?: string, name?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/dev/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email || 'dev@artstash.local',
          name: name || 'Dev User',
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Dev login failed');
      }
      const data = await response.json();
      storeTokens(data);
    } catch (err: any) {
      setError(err.message || 'Dev login failed');
    } finally {
      setIsLoading(false);
    }
  }, [storeTokens]);

  // Redirect to Keycloak login
  const loginWithKeycloak = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const loginUrl = KEYCLOAK_REDIRECT_URI
        ? `${API_URL}/api/auth/keycloak/login-url?redirect_uri=${encodeURIComponent(KEYCLOAK_REDIRECT_URI)}`
        : `${API_URL}/api/auth/keycloak/login-url`;

      const response = await fetch(loginUrl);
      if (!response.ok) {
        const data = typeof (response as any).json === 'function'
          ? await response.json().catch(() => ({}))
          : {};
        throw new Error(data.detail || 'Failed to get login URL');
      }
      const data = await response.json();
      window.location.href = data.url;
    } catch (err: any) {
      setError(err.message || 'Failed to initiate login');
      setIsLoading(false);
    }
  }, []);

  // Exchange authorization code for tokens
  const handleCallback = useCallback(async (code: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const body = KEYCLOAK_REDIRECT_URI
        ? { code, redirect_uri: KEYCLOAK_REDIRECT_URI }
        : { code };

      const response = await fetch(`${API_URL}/api/auth/keycloak/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Authentication failed');
      }
      const data = await response.json();
      storeTokens(data);
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [storeTokens]);

  // Refresh the access token using the stored refresh token
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${API_URL}/api/auth/keycloak/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) return false;
      const data = await response.json();
      storeTokens(data);
      return true;
    } catch {
      return false;
    }
  }, [storeTokens]);

  // Logout — clear local state and redirect to Keycloak logout
  const logout = useCallback(async () => {
    analytics.clearIdentity();
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    setToken(null);
    setUser(null);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const redirectUri = window.location.origin + '?logged_out=true';
    try {
      const response = await fetch(
        `${API_URL}/api/auth/keycloak/logout-url?redirect_uri=${encodeURIComponent(redirectUri)}`
      );
      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.warn('Keycloak logout redirect unavailable; local session cleared', error);
    }
  }, []);

  // Schedule token refresh at 80% of token lifetime
  useEffect(() => {
    if (!token) return;

    const expiryStr = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryStr) return;

    const expiry = parseInt(expiryStr, 10);
    const now = Date.now();
    const timeUntilRefresh = (expiry - now) * 0.8;

    if (timeUntilRefresh <= 0) {
      // Token expired or nearly expired — refresh immediately
      refreshAccessToken().then(success => {
        if (!success) logout();
      });
      return;
    }

    refreshTimer.current = setTimeout(async () => {
      const success = await refreshAccessToken();
      if (!success) logout();
    }, timeUntilRefresh);

    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [token, refreshAccessToken, logout]);

  return {
    token,
    user,
    isAuthenticated,
    isLoading,
    error,
    authMode,
    loginWithKeycloak,
    loginDev,
    handleCallback,
    refreshAccessToken,
    logout,
  };
}
